import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { toast } from "@/components/toast/toast-queue";
import apiClient from "@/lib/api/client";

import type { ChatStory } from "../types";

interface AttachmentData {
  attachmentId: number;
  name: string;
}

interface EditModeState {
  attachments: AttachmentData[];
  commentBatchId: null | string;
  isEditing: boolean;
  messageId: null | number;
  messages: ChatStory[];
}

interface UpdateChatCommentParams {
  attachments?: AttachmentData[];
  comment: string;
  commentBatchId?: null | string;
  id: number;
  parentId?: number;
  shouldCreateNewMessage?: boolean;
}

interface UseUpdateChatCommentReturn {
  addAttachment: (attachment: AttachmentData) => void;
  cancelEdit: () => void;
  editMode: EditModeState;
  isEditing: boolean;
  removeAttachment: (attachmentId: number) => void;
  startEdit: (messageId: number, batchId: string) => void;
  updateComment: UseMutationResult<Response, Error, UpdateChatCommentParams>;
}

export function useUpdateChatComment({
  rawMessages,
  requestId,
  userId,
}: {
  rawMessages: ChatStory[];
  requestId: number;
  userId: number;
}): UseUpdateChatCommentReturn {
  const [editMode, setEditMode] = useState<EditModeState>({
    attachments: [],
    commentBatchId: null,
    isEditing: false,
    messageId: null,
    messages: [],
  });

  const updateComment = useMutation({
    mutationFn: async ({
      attachments,
      comment,
      commentBatchId,
      id,
      shouldCreateNewMessage,
    }: UpdateChatCommentParams) =>
      apiClient
        .url(`/stories/chat/${id}`)
        .patch({
          attachments,
          comment,
          commentBatchId,
          id,
          parentId: requestId,
          shouldCreateNewMessage,
          userId,
        })
        .res(),
    onError: (error: Error) => {
      console.error("Failed to update chat comment:", error);

      toast.add(
        {
          description: error.message || "Please try again later",
          title: "Error updating message",
          type: "error",
        },
        {
          timeout: 5000,
        },
      );
    },
    onSuccess: () => {
      toast.add(
        {
          title: "Message updated successfully",
          type: "success",
        },
        {
          timeout: 3000,
        },
      );
    },
  });

  useEffect(() => {
    if (editMode.commentBatchId) {
      const messages = rawMessages.filter(
        (message) => message.commentBatchId === editMode.commentBatchId,
      );

      setEditMode((prev) => ({
        ...prev,
        messages,
      }));
    }
  }, [editMode.commentBatchId, rawMessages]);

  const startEdit = (messageId: number, commentBatchId: string) => {
    setEditMode({
      attachments: [],
      commentBatchId,
      isEditing: true,
      messageId,
      messages: [],
    });
  };

  const cancelEdit = () => {
    setEditMode({
      attachments: [],
      commentBatchId: null,
      isEditing: false,
      messageId: null,
      messages: [],
    });
  };

  const addAttachment = (attachment: AttachmentData) => {
    setEditMode((prev) => ({
      ...prev,
      attachments: [...prev.attachments, attachment],
    }));
  };

  const removeAttachment = (attachmentId: number) => {
    setEditMode((prev) => ({
      ...prev,
      attachments: prev.attachments.filter(
        (att) => att.attachmentId !== attachmentId,
      ),
    }));
  };

  return {
    addAttachment,
    cancelEdit,
    editMode,
    isEditing: editMode.isEditing,
    removeAttachment,
    startEdit,
    updateComment,
  };
}
